import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "@utils/cn";
import { ChevronDown } from "lucide-react";
import { MenuItem } from "@/core/types";
import {
  isFeatureEnabled,
  subscribeToFeatureFlags,
} from "@/core/config/featureFlags";
import { menuItems } from "@/core/config/menuConfig";
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubItem,
} from "@/components/atoms/Sidebar";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@radix-ui/react-collapsible";
import { useSidebar } from "@/core/context/SideBarContext";

interface NavigationMenuProps {
  isMobile: boolean;
}

export const NavigationMenu = ({
  isMobile,
}: NavigationMenuProps) => {
  const { toggleSidebar } = useSidebar();
  const location = useLocation();
  const [filteredItems, setFilteredItems] = useState<MenuItem[]>([]);

  // Filtra los items basado en sus feature flags
  const filterItem = (item: MenuItem): boolean => {
    if (!item.featureFlag) return true;
    // Convertimos el valor a string para evitar problemas de tipos
    const flagName = item.featureFlag.toString();
    return isFeatureEnabled(flagName as any);
  };

  // Efecto para filtrar los elementos del menú cuando cambian los feature flags
  useEffect(() => {
    // Función para filtrar elementos del menú basados en feature flags
    const filterMenuItems = () => {
      const filtered = menuItems.filter(filterItem);
      setFilteredItems(filtered);
    };
    // Filtrar inicialmente
    filterMenuItems();
    // Suscribirse a los cambios en los feature flags
    const unsubscribe = subscribeToFeatureFlags(() => {
      // Re-filtrar los elementos del menú cuando cambia cualquier flag
      filterMenuItems();
    });
    // Limpiar la suscripción cuando se desmonta el componente
    return () => {
      unsubscribe();
    };
  }, []);

  // Control de submenús expandidos
  const [expandedSubmenus, setExpandedSubmenus] = useState<string[]>([]);

  // Función para verificar si una ruta está activa
  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  // Función para manejar la expansión de submenús
  const toggleSubmenu = (id: string) => {
    setExpandedSubmenus((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const renderMenuItem = (item: MenuItem) => {
    // Filter subitems based on feature flags
    const filteredSubItems = item.children?.filter(filterItem) || [];
    const hasSubItems = filteredSubItems.length > 0;
    const isItemActive = isActive(item.path || "");
    const isSubmenuExpanded = expandedSubmenus.includes(item.path || "");
    const hasActiveChild = filteredSubItems.some((subItem) =>
      isActive(subItem.path || "")
    );

    return (
      <SidebarGroup key={item.path}>
        <SidebarGroupContent>
          <SidebarMenu>
            {!hasSubItems
              ?
              <SidebarMenuButton className={isItemActive ? "bg-[--primary-500] text-white" : ""}>
                <Link
                  to={item.path || "#"}
                  className="flex items-center gap-2"
                  onClick={() => {
                    if (isMobile) {
                      toggleSidebar();
                    }
                  }}
                >
                  {item.icon && (
                    <span className={cn("w-5 h-5", isItemActive && "text-white")}>{item.icon}</span>
                  )}
                  <span className={cn(
                    "font-medium text-sm",
                    isItemActive || hasActiveChild ? "text-white" : ""
                  )}>
                    {item.label}
                  </span>
                </Link>
              </SidebarMenuButton>
              : <Collapsible
                open={isSubmenuExpanded || isItemActive || hasActiveChild}
                onOpenChange={() => toggleSubmenu(item.path || "")}
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className={cn(
                        "flex items-center justify-start gap-2",
                        isItemActive && "bg-[--primary-500] text-white"
                      )}
                    >
                      <>
                        <span className="w-5 h-5">
                          {item.icon && item.icon}
                        </span>
                        <span className={cn(
                          "font-medium text-sm",
                          isItemActive || hasActiveChild ? "text-white" : ""
                        )}>
                          {item.label}
                        </span>
                        <ChevronDown
                          className={cn(
                            "h-4 w-4 transition-transform duration-200 ml-auto",
                            isSubmenuExpanded ? "rotate-180" : ""
                          )}
                        />
                      </>
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent className="space-y-2">
                    {filteredSubItems.map((subItem) => {
                      const isSubItemActive = isActive(subItem.path || "");
                      return (
                        <SidebarMenuSub
                          key={subItem.path}>
                          <SidebarMenuSubItem>
                            <Link
                              to={subItem.path || "#"}
                              className="flex items-center gap-2"
                              onClick={() => {
                                console.log(subItem.path);
                                if (isMobile) {
                                  toggleSidebar();
                                }
                              }}>
                              {subItem.icon && (
                                <span className={cn("w-4 h-4", isSubItemActive && "text-[--primary-500]")}>
                                  {subItem.icon}
                                </span>
                              )}
                              <span className={cn("font-small text-sm", isSubItemActive && "text-[--primary-500]")}>{subItem.label}</span>
                            </Link>
                          </SidebarMenuSubItem>
                        </SidebarMenuSub>
                      );
                    })}
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            }
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  return (
    <SidebarContent>
      {filteredItems.map(renderMenuItem)}
    </SidebarContent>
  );
};
